import { Chip } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Link, generatePath } from "react-router-dom";
import ProjectStatusBadge from "../../../../Common/components/ProjectStatusBadge/ProjectStatusBadge";
import { ProjectStatus } from "../../../../Common/models/project";
import { CapabilityTool } from "../../../../Common/models/tools";
import GridTools from "../GridTools/GridTools";
import ProjectGridActionButton from "../ProjectGridActionButton/ProjectGridActionButton";
import { orderTools } from "../../../../Common/utils/toolUtil";
import routes from "../../../../Common/consts/routes";
import TruncatedGridCell from "../../../../Common/components/TruncatedGridCell";
import TruncatedTooltip from "../../../../Common/components/TruncatedTooltip";

const getStatusColor = (
  status: string
):
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning" => {
  switch (status) {
    case "Active":
      return "primary";
    case "Processing":
      return "info";
    case "Blocked":
      return "error";

    default:
      return "warning";
  }
};

export const getColumns = (callForceRefresh?: any): GridColDef[] => {
  const baseColumns = [
    {
      field: "name",
      headerName: "Project name",
      disableColumnMenu: true,
      flex: 2,
      renderCell: (params: GridRenderCellParams<string>) => {
        const name = params.row.name;
        const isNew = params.row.isNew;
        const projectId = params.row.id;

        const projectUrl = generatePath(routes.projectDetails, {
          id: projectId.toString(),
          tab: "details",
        });

        return (
          <TruncatedTooltip title={name} placement="top-start">
            <span
              style={{
                width: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              <Link
                to={projectUrl}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) =>
                  ((e.target as HTMLElement).style.textDecoration = "underline")
                }
                onMouseLeave={(e) =>
                  ((e.target as HTMLElement).style.textDecoration = "none")
                }
              >
                {name}
              </Link>
              {!!isNew && (
                <Chip
                  sx={{ ml: "12px" }}
                  size="small"
                  color="info"
                  label="New"
                />
              )}
            </span>
          </TruncatedTooltip>
        );
      },
    },

    {
      field: "productOwner",
      headerName: "Product owner",
      disableColumnMenu: true,
      flex: 0.8,
      renderCell: (params: GridRenderCellParams<string>) => {
        const productOwner = params.value as string;
        return <TruncatedGridCell value={productOwner} placement="top-start" />;
      },
    },

    {
      field: "costCentre",
      headerName: "Cost center",
      flex: 0.9,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<string>) => {
        const costCenter = params.value as string;
        return <TruncatedGridCell value={costCenter} placement="top-start" />;
      },
    },

    {
      field: "tools",
      headerName: "Tools",
      width: 180,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<string>) => {
        if (!params.row.tools) {
          return <></>;
        }
        const tools: CapabilityTool[] = params.row.tools.split(",");
        return <GridTools tools={orderTools(tools)} />;
      },
    },

    {
      field: "status",
      headerName: "Status",
      width: 110,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<string>) => {
        const status = params.row.status as ProjectStatus;

        return <ProjectStatusBadge status={status} />;
      },
    },
  ];

  if (callForceRefresh) {
    return [
      ...baseColumns,
      {
        field: "projectAction",
        headerName: "",
        width: 60,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<string>) => {
          const id = params.row.id;
          const status = params.row.status as ProjectStatus;

          return (
            <ProjectGridActionButton
              projectId={id}
              status={status}
              callForceRefresh={callForceRefresh}
            />
          );
        },
      },
    ];
  }

  return baseColumns;
};
